import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { FunctionComponent } from 'react';
import { reduceResolution, wbp } from 'Theme';

interface OverFullWindowHeaderProps {
    title?: string;
    className?: string;
    showMobileLogo?: boolean;
    showButtonOnHeaderRight?: React.ReactNode;
    onClose?: () => void;
    onBack?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            borderBottom: '1px solid #DDDDDD;',

            [theme.breakpoints.down(wbp)]: {
                minHeight: `${reduceResolution(66)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                minHeight: '66px'
            }
        },
        leftContainer: {
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingLeft: 16,

            [theme.breakpoints.up('md')]: {
                paddingLeft: '32px'
            }
        },
        rightContainer: {
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: 14,

            [theme.breakpoints.up('md')]: {
                paddingRight: '32px'
            }
        },
        logo: {
            [theme.breakpoints.down(wbp)]: {
                height: `${reduceResolution(42)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                height: '42px'
            }
        },
        title: {
            fontSize: 18,
            lineHeight: 1.2,
            fontWeight: 500,
            fontFamily: 'Poppins',
            color: '#000000',
            textAlign: 'center',

            [theme.breakpoints.up('md')]: {
                fontSize: 20
            }
        },
        closeIcon: {
            cursor: 'pointer',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(24)}px !important`,
                height: `${reduceResolution(24)}px !important`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '24px !important',
                height: '24px !important'
            }
        },
        buttonOnHeaderRight: {
            marginRight: 15
        },
        backButton: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            color: '#000000',
            fontFamily: 'Poppins',
            fontSize: 16,
            fontWeight: 500,
            lineHeight: 1,
            [theme.breakpoints.down('sm')]: {
                gap: 0
            }
        },
        backLabel: {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        backIcon: {
            fontSize: 16
        }
    })
);
export const OverFullWindowHeader: FunctionComponent<OverFullWindowHeaderProps> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showBack = Boolean(props.onBack);
    const showLogo = !showBack && (!isMobile || props.showMobileLogo);

    return (
        <Box className={clsx(classes.container, props.className)}>
            {(showBack || showLogo) && (
                <Box className={classes.leftContainer}>
                    {showBack ? (
                        <button
                            type="button"
                            className={classes.backButton}
                            onClick={props.onBack}
                        >
                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                className={classes.backIcon}
                            />
                            <span className={classes.backLabel}>Back</span>
                        </button>
                    ) : (
                        <img
                            src={isMobile ? '/images/logo/cuddles-isotype.svg' : '/images/logo/logo.png'}
                            alt="Logo"
                            className={classes.logo}
                        />
                    )}
                </Box>
            )}

            <Box
                className={clsx({
                    [classes.leftContainer]: isMobile && !showBack && props.showMobileLogo !== true
                })}
            >
                {props.title && <Typography className={classes.title}>{props.title}</Typography>}
            </Box>

            <Box className={classes.rightContainer}>
                {props.showButtonOnHeaderRight && (
                    <Box className={classes.buttonOnHeaderRight}>{props.showButtonOnHeaderRight}</Box>
                )}
                {props.onClose && (
                    <FontAwesomeIcon
                        icon={faTimes}
                        className={classes.closeIcon}
                        onClick={event => {
                            event.stopPropagation();
                            props.onClose && props.onClose();
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default OverFullWindowHeader;
